import React, { useEffect, useState, useRef } from "react";
import { Skeleton } from "@mui/material";

interface AdSlotProps {
  slot: string; // Internal identifier (e.g., "top", "left")
  dataAdSlot?: string; // The Google AdSense slot ID
  dataAdClient?: string; // The Google AdSense client ID (e.g., "ca-pub-xxx")
  format?: "horizontal" | "vertical" | "auto" | string;
  width?: number;
  height?: number;
  className?: string;
  loading?: boolean;
}

/**
 * Google AdSense Ad Slot component.
 */
const AdSlot: React.FC<AdSlotProps> = ({
  slot,
  dataAdSlot,
  dataAdClient = process.env.REACT_APP_ADSENSE_CLIENT_ID,
  format = "auto",
  width,
  height,
  className,
  loading: loadingProp,
}) => {
  const [internalLoading, setInternalLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!dataAdSlot || !dataAdClient || !containerRef.current || pushedRef.current) return;

    const container = containerRef.current;
    
    const initAd = () => {
      if (pushedRef.current) return;
      // Double check dimensions right before pushing
      if (container.offsetWidth === 0 || container.offsetHeight === 0) return;
      
      try {
        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushedRef.current = true;
      } catch (e: any) {
        console.error("AdSense push error:", e);
      }
    };

    // Use ResizeObserver to wait for the element to have dimensions
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          // Wait a tick for layout to settle completely before pushing
          setTimeout(() => {
            if (!pushedRef.current && container.offsetWidth > 0) {
              initAd();
              observer.disconnect(); // Only need to trigger once
            }
          }, 150);
        }
      }
    });

    observer.observe(container);

    // Cleanup
    return () => observer.disconnect();
  }, [dataAdSlot, dataAdClient]);

  useEffect(() => {
    if (loadingProp !== undefined) return;
    const t = setTimeout(() => setInternalLoading(false), 600);
    return () => clearTimeout(t);
  }, [loadingProp]);

  const isLoading = loadingProp ?? internalLoading;

  const style: React.CSSProperties = {};
  if (width) style.maxWidth = `${width}px`;
  if (height) style.minHeight = `${height}px`;

  // If we have real AdSense credentials, render the ins tag
  if (dataAdSlot && dataAdClient) {
    return (
      <div
        ref={containerRef}
        className={
          "relative w-full mx-auto flex items-center justify-center overflow-hidden " +
          (className ?? "")
        }
        style={style}
      >
        <ins
          className="adsbygoogle"
          style={{ display: "block", width: "100%", height: "100%", ...style }}
          data-ad-client={dataAdClient}
          data-ad-slot={dataAdSlot}
          data-ad-format={format}
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  // Fallback to placeholder/skeleton
  return (
    <div
      ref={containerRef}
      data-ad-slot={slot}
      role="complementary"
      aria-label="Advertisement"
      style={style}
      className={
        "relative w-full mx-auto flex items-center justify-center border border-dashed border-[#d0d4dd] bg-[#f7f8fa] text-[#98a1b2] text-xs font-medium font-['Inter'] rounded-md overflow-hidden " +
        (className ?? "")
      }
    >
      {isLoading ? (
        <Skeleton variant="rectangular" className="absolute inset-0 h-full w-full rounded-md" sx={{ bgcolor: '#e9ecf2' }} />
      ) : (
        <div className="flex flex-col items-center justify-center py-3 px-4 text-center z-10">
          <span className="uppercase tracking-wider">Advertisement</span>
          {width && height ? (
            <span className="text-[10px] opacity-70 mt-0.5">
              {width}×{height}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AdSlot;
